import React, { useState, useEffect, useRef } from "react";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import styles from "./leaderboard.module.scss";
import "swiper/css";

import { capitalize } from "lodash";

import ExpandArrowIcon from "../../assets/icons-v2/expand_arrow.png";
import CustomTabPanel from "../../components/common/CustomTabPanel";
import Classic from "./components/Classic";
import Puzzle from "./components/Puzzle";

import { LIVE_VARIANT_VERSIONS } from "../../constants/live-variant-versions";

import { useUser } from "../../context/UserContext";
import useMixpanelEvent from "../../hooks/useMixpanelEvent";
import useLocalStorage from "../../hooks/useLocalStorage";

const Leaderboard = () => {
    const { userState, setUserState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [visitorStatus] = useLocalStorage("visitor-status");
    const lastTrackedRef = useRef(null);
    const versions = Object.values(LIVE_VARIANT_VERSIONS);
    let selectedVersion = userState?.leaderboardSelectedVersion || 'classic';
    // Find the index of the selected version
    let tabValue = versions.findIndex(v => v.url === selectedVersion);
    if (tabValue === -1) tabValue = versions.findIndex(v => v.url === 'classic');
    // On mount, check localStorage for leaderboardSelectedVersion and use it if present
    useEffect(() => {
        const stored = localStorage.getItem("leaderboardSelectedVersion");
        if (stored) {
            setUserState({ ...userState, leaderboardSelectedVersion: stored });
            localStorage.removeItem("leaderboardSelectedVersion");
        }
    }, [setUserState, userState]);

    // Mixpanel Page View for '/leaderboard' with a 5-minute throttle window
    useEffect(() => {
        const path = window.location.pathname;
        if (path !== "/leaderboard") return;
        if (lastTrackedRef.current === path) return;
        const key = `page_view_tracked:${path}`;
        const now = Date.now();
        const lastTsRaw = sessionStorage.getItem(key);
        const lastTs = lastTsRaw ? Number(lastTsRaw) : 0;
        const FIVE_MIN_MS = 5 * 60 * 1000;
        if (now - lastTs < FIVE_MIN_MS) return;

        const selectedVersion = userState?.leaderboardSelectedVersion || localStorage.getItem("leaderboardSelectedVersion") || "puzzle";
        const versionLabel = selectedVersion === "classic" ? "Classic" : "Puzzle";

        sendMixpanelEvent("Page View", {
            isAuthenticated: !!userState?.id,
            visitorStatus: userState?.id ? null : (visitorStatus || "new-guest"),
            pageName: `Leaderboard - ${versionLabel}`,
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.email_address || null,
            pageUrl: window.location.href,
        });

        lastTrackedRef.current = path;
        sessionStorage.setItem(key, String(now));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDropdown = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSelect = (event, newTabValue) => {
        setUserState({ ...userState, leaderboardSelectedVersion: versions[newTabValue].url });
        setAnchorEl(null);
    };

    return (
        <div className={styles.container + " !tw-px-4"}>
            <div className="tw-flex tw-gap-1 tw-mb-[-10px]">
                <h3 className="leaderboard-title">Leaderboard:</h3>
                <div
                    className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer"
                    onClick={handleDropdown}
                >
                    <h3 className="leaderboard-title">{versions[tabValue]?.label ? capitalize(versions[tabValue].label) : capitalize(versions[tabValue]?.url)}</h3>
                    <img className="tw-mb-[.5em]" src={ExpandArrowIcon} alt="expand-arrow-icon" />
                </div>

                <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "basic-button",
                    }}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    sx={{ mt: -1 }}
                >
                    {versions.map((version, index) => (
                        <MenuItem
                            onClick={(e) => handleSelect(e, index)}
                            selected={tabValue === index}
                            disabled={version?.disableInProfile}
                        >
                            {version?.disableInProfile
                                ? `${capitalize(version.label || version.url)} (Coming soon)`
                                : capitalize(version.label || version.url)}
                        </MenuItem>
                    ))}
                </Menu>
            </div>

            <CustomTabPanel value={tabValue} index={versions.findIndex(v => v.url === 'classic')}>
                <Classic />
            </CustomTabPanel>
            <CustomTabPanel value={tabValue} index={versions.findIndex(v => v.url === 'puzzle')}>
                <Puzzle version={versions[tabValue]} />
            </CustomTabPanel>
        </div>
    );
};

export default Leaderboard;
